import { useCallback } from "react";
import type { FieldErrors, SubmitHandler } from "react-hook-form";
import { useStatusToasts } from "../../../../../../../hooks/useStatusToasts";
import { useSubmissionsBuilderContext } from "../../SubmissionsBuilderContext";
import type { ExistingSubmissionType } from "../../types";
import {
  type SubmissionsBuilderFormData,
  useBuilderFormContext,
} from "../form";
import { useCreateSubmissionType } from "./create";
import { useUpdateSubmissionType } from "./update";

export function useSubmitForm() {
  const { existingSubmissionType } = useSubmissionsBuilderContext();
  const { handleSubmit } = useBuilderFormContext();
  const onSubmit = useOnSubmitForm({ existingSubmissionType });
  const { addErrorToast } = useStatusToasts();

  return useCallback(
    (
      onError?: (errors: FieldErrors<SubmissionsBuilderFormData>) => void,
      event?: React.BaseSyntheticEvent,
    ) => {
      function defaultOnError(errors: FieldErrors<SubmissionsBuilderFormData>) {
        // intentionally logging the errors to help sup-dev debug
        console.log(
          "Form validation errors:",
          JSON.stringify(errors, (key, value) =>
            key === "ref" ? undefined : value,
          ),
          2,
        );
        addErrorToast(
          "The form is invalid. Please check the fields and try again.",
        );
      }

      return handleSubmit(onSubmit, onError ?? defaultOnError)(event);
    },
    [addErrorToast, handleSubmit, onSubmit],
  );
}

type OnSubmitSubmissionTypeVersionParams = {
  existingSubmissionType?: Maybe<ExistingSubmissionType>;
};

function useOnSubmitForm({
  existingSubmissionType,
}: OnSubmitSubmissionTypeVersionParams): SubmitHandler<SubmissionsBuilderFormData> {
  const updateSubmissionType = useUpdateSubmissionType();
  const createSubmissionType = useCreateSubmissionType();

  if (existingSubmissionType) {
    return updateSubmissionType(existingSubmissionType);
  }
  return createSubmissionType;
}
